/**
 * Spec 77 Phase C — WorkflowDraftCard.
 *
 * Renders an inline card in the conversation message list when the server
 * posts a system message with a `workflowDraftCreated` envelope — i.e. when
 * the WorkflowDrafter has created a draft workflow_definitions row.
 *
 * `parseWorkflowDraftMessage` is the companion parser — mirrors the defensive
 * style of `parseCloseSummary` in CloseSummaryCard.tsx.
 *
 * The "Open in builder" link sets `?panel=workflow:<id>` on the current
 * conversation URL, preserving all other search params. This opens the
 * workflow panel inline via Spec 76's panel host.
 */
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/Button.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkflowDraftPayload {
  id: number;
  name: string;
}

// ─── Parser ───────────────────────────────────────────────────────────────────

/**
 * Parse a system-message `content` string into a WorkflowDraftPayload.
 *
 * Returns `null` when:
 * - content is not valid JSON
 * - the `workflowDraftCreated` envelope is absent or not an object
 * - `id` is missing or not a positive integer
 * - `name` is missing or not a non-empty string
 *
 * Extra/surplus keys in the payload object are silently ignored.
 */
export function parseWorkflowDraftMessage(content: string): WorkflowDraftPayload | null {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;

    const root = parsed as Record<string, unknown>;
    const envelope = root.workflowDraftCreated;
    if (typeof envelope !== 'object' || envelope === null) return null;

    const e = envelope as Record<string, unknown>;
    const id = typeof e.id === 'number' ? e.id : null;
    const name = typeof e.name === 'string' ? e.name : null;

    if (id === null || !Number.isInteger(id) || id <= 0) return null;
    if (!name) return null;

    return { id, name };
  } catch {
    return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface WorkflowDraftCardProps extends WorkflowDraftPayload {}

export const WorkflowDraftCard: React.FC<WorkflowDraftCardProps> = ({ id, name }) => {
  const [, setSearchParams] = useSearchParams();

  const handleOpen = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('panel', `workflow:${id}`);
        return next;
      },
      { replace: false },
    );
  }, [id, setSearchParams]);

  return (
    <div className="bg-background-gray-secondary_alt border border-card-border rounded-xl px-4 py-3 space-y-1">
      {/* Heading */}
      <p className="text-sm text-title-50 font-medium">Workflow draft created</p>

      {/* Workflow name */}
      <p className="text-sm text-text-secondary">{name}</p>

      {/* Open link */}
      <Button
        variant="ghost"
        size="sm"
        className="h-auto p-0 text-primary-500 hover:underline"
        onClick={handleOpen}
        aria-label={`Open workflow draft "${name}" in builder`}
      >
        Open in builder
      </Button>
    </div>
  );
};
